import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api-client';
import { formatRelativeTime } from '../utils/timeFormat';
import { useUIState } from '../contexts/UIStateContext';
import Logo from './Logo';
import UserProfileBand from './shared/UserProfileBand';
import NotificationBanners from './shared/NotificationBanners';
import { IconButton, NavButton } from './shared/StandardButtons';

export default function ConversationSidebar({ isCollapsed = false, products = [], onCreateProduct }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setChatLastConversation } = useUIState();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentConversationId, setCurrentConversationId] = useState(null);

  // Extract conversation ID from URL if present
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'home' && pathParts[2]) {
      setCurrentConversationId(pathParts[2]);
    } else {
      setCurrentConversationId(null);
    }
  }, [location.pathname]);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Listen for conversation updates from WebSocket
  useEffect(() => {
    const handleConversationUpdate = (event) => {
      // Refresh conversation list when a conversation is created or updated
      fetchConversations();
    };

    window.addEventListener('conversationUpdated', handleConversationUpdate);

    return () => {
      window.removeEventListener('conversationUpdated', handleConversationUpdate);
    };
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/v1/chat/conversations');
      setConversations(data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConversation = async (conversationId, event) => {
    event.stopPropagation(); // Prevent navigation when clicking delete

    try {
      await api.delete(`/api/v1/chat/conversations/${conversationId}`);

      // Remove from local state
      setConversations(conversations.filter(conv => conv.id !== conversationId));

      // If we deleted the current conversation, navigate to home (no conversation)
      if (conversationId === currentConversationId) {
        navigate('/home', { replace: true });
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const handleConversationClick = (conversationId) => {
    navigate(`/home/${conversationId}`);
  };

  const handleLogoClick = () => {
    // Clear the last conversation so it doesn't auto-restore
    setChatLastConversation(null);

    // If we're already at /home (with or without conversation), force a full page reload
    // This ensures WebSocket connection and state are fully reset
    if (location.pathname.startsWith('/home')) {
      window.location.href = '/home';
    } else {
      // Otherwise just navigate normally
      navigate('/home', { replace: true });
    }
  };

  // Sort conversations: current one first, then by updated_at
  const sortedConversations = [...conversations].sort((a, b) => {
    if (a.id === currentConversationId) return -1;
    if (b.id === currentConversationId) return 1;
    return new Date(b.updated_at) - new Date(a.updated_at);
  });

  return (
    <div className="h-full flex flex-col relative">
      {/* Top Section - Same height as header */}
      <div className="h-16 border-b border-gray-700 px-4 flex items-center">
        <button
          onClick={handleLogoClick}
          className="flex-shrink-0 transition-opacity duration-200 hover:opacity-80"
          title="Home"
        >
          <Logo
            alt="itgetsit logo"
            className="h-10"
          />
        </button>
      </div>

      {/* Notification Banners */}
      <NotificationBanners onCreateProduct={onCreateProduct} />

      {/* Chat History Section */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Recent Chats
          </h2>
          <IconButton onClick={() => navigate('/home')} title="New Chat" className="p-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </IconButton>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="px-3 py-3 rounded-lg bg-gray-800/50 animate-pulse">
                <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm text-gray-500">No conversations yet</p>
            <p className="text-xs text-gray-600 mt-1">Start a new chat to begin</p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedConversations.map((conv) => {
              const isCurrent = conv.id === currentConversationId;
              return (
                <div
                  key={conv.id}
                  onClick={() => handleConversationClick(conv.id)}
                  className={`group relative px-3 py-3 rounded-lg text-sm transition-all cursor-pointer border`}
                  style={isCurrent ? {
                    backgroundColor: 'var(--color-accent-primary)',
                    borderColor: 'var(--color-accent-secondary)',
                    color: 'var(--color-text-primary)'
                  } : {
                    borderColor: 'transparent',
                    color: 'var(--color-text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                      e.currentTarget.style.color = 'var(--color-text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--color-text-secondary)';
                    }
                  }}
                  title={conv.title}
                >
                  {/* Title - truncated with tooltip */}
                  <div className="font-medium truncate mb-1 group-hover:pr-10 transition-all">
                    {conv.title}
                  </div>

                  {/* Message count and time */}
                  <div className="flex items-center text-xs gap-2" style={{ color: 'var(--color-text-muted)' }}>
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      {conv.message_count}
                    </span>
                    <span>•</span>
                    <span>{formatRelativeTime(conv.updated_at)}</span>
                  </div>

                  {/* Delete Button - appears on hover, vertically centered */}
                  <button
                    onClick={(e) => handleDeleteConversation(conv.id, e)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{
                      color: 'var(--color-text-muted)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                      e.currentTarget.style.color = 'var(--color-error)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--color-text-muted)';
                    }}
                    title="Delete conversation"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Navigation Band */}
      <div className="border-t border-gray-700 px-4 py-3 flex items-center gap-2">
        <NavButton
          onClick={() => navigate('/knowledge')}
          title="Library"
          label="Library"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          }
        />
        <NavButton
          onClick={() => navigate('/questions')}
          title="Forum"
          label="Forum"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          }
        />
      </div>

      {/* User Profile Band */}
      <UserProfileBand />
    </div>
  );
}
